import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useDictation. Browser-native speech-to-text via Web Speech API.
 *
 * Different from the ElevenLabs voice agent: no AI conversation, no audio
 * streamed to a backend. The browser transcribes locally and we push the
 * resulting text into a controlled input. Works in Chromium + Safari (incl. iOS).
 * Firefox does not implement SpeechRecognition, `isSupported` returns false.
 *
 * Usage:
 *   const { isSupported, isListening, start, stop, transcript, interimTranscript, reset, error }
 *     = useDictation({ lang: 'nl-NL', onResult: (final, interim) => ... });
 *
 *   `transcript` accumulates finalized utterances across the session.
 *   `interimTranscript` is the in-progress hypothesis that may still change.
 */
export function useDictation({ lang = 'nl-NL', onResult } = {}) {
  const SpeechRecognition =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;

  const isSupported = !!SpeechRecognition;
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  // Track whether the user explicitly stopped, vs. browser auto-ending
  // (Chrome cuts the stream after ~60s of silence). When auto-ended we
  // restart so the user doesn't lose their listening session.
  const userStoppedRef = useRef(false);

  useEffect(() => {
    if (!isSupported) return;
    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let finalChunk = '';
      let interimChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          finalChunk += result[0].transcript;
        } else {
          interimChunk += result[0].transcript;
        }
      }
      if (finalChunk) {
        const next = (transcriptRef.current + ' ' + finalChunk).trim();
        transcriptRef.current = next;
        setTranscript(next);
        if (onResultRef.current) onResultRef.current(next, interimChunk);
      }
      setInterimTranscript(interimChunk);
    };

    recognition.onerror = (event) => {
      // 'no-speech' fires often when user is just thinking, not a real error.
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      setError(event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      // Auto-restart if user didn't explicitly stop (handles 60s silence cutoff)
      if (!userStoppedRef.current && recognitionRef.current === recognition) {
        try {
          recognition.start();
          return;
        } catch (_) {
          // start() throws if recognition is already running, fall through.
        }
      }
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;
    return () => {
      userStoppedRef.current = true;
      try { recognition.stop(); } catch (_) {}
      recognitionRef.current = null;
    };
  }, [isSupported, lang, SpeechRecognition]);

  const start = useCallback(() => {
    if (!recognitionRef.current || isListening) return;
    userStoppedRef.current = false;
    setError(null);
    setInterimTranscript('');
    try {
      recognitionRef.current.start();
      setIsListening(true);
    } catch (err) {
      setError(err.message || 'start_failed');
    }
  }, [isListening]);

  const stop = useCallback(() => {
    if (!recognitionRef.current) return;
    userStoppedRef.current = true;
    try { recognitionRef.current.stop(); } catch (_) {}
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const reset = useCallback(() => {
    transcriptRef.current = '';
    setTranscript('');
    setInterimTranscript('');
  }, []);

  return { isSupported, isListening, start, stop, reset, transcript, interimTranscript, error };
}
